import { useAppSelector } from '@/store/hooks';
import { selectGreenroomGraphics, selectGreenroomMedia, selectGreenroomShots } from '@/store/slices/project/selectors';
import { selectStylepackContent } from '@/store/slices/stylepacks/selectors';
import { useEffect, useMemo, useState } from 'react';
import { createTemplateTypeToShotMap, getContentTemplateDuration, getGraphicDuration } from './stylepack-utils';
import { toGraphicItem } from './stylepack-media-to-graphic-item';
import type { GraphicItem } from '../components/video-list-graphic.types';
import { VideoType, videoTypeMap, type Stylepack } from '@/types/stylepacks.types';

export const useGreenroomStylepackShots = (stylepack: Stylepack | undefined, type?: VideoType): GraphicItem[] => {
  const [durations, setDurations] = useState<Record<string, number>>({});

  const greenroomGraphics = useAppSelector(selectGreenroomGraphics);
  const greenroomMedia = useAppSelector(selectGreenroomMedia);
  const greenroomStylepackContent = useAppSelector(selectStylepackContent);
  const greenroomShots = useAppSelector(selectGreenroomShots);

  const templateTypeToShotMap = useMemo(() => {
    return createTemplateTypeToShotMap(greenroomGraphics, greenroomShots, greenroomMedia);
  }, [greenroomGraphics, greenroomMedia, greenroomShots]);

  const graphicItems = useMemo(() => {
    // combine graphics and media into single array
    const itemArray = [...(greenroomMedia || []), ...(greenroomGraphics || [])];

    // unify the types for rendering
    return itemArray
      .map(toGraphicItem(greenroomStylepackContent, templateTypeToShotMap))
      .filter<GraphicItem>((item) => !!item);
  }, [greenroomGraphics, greenroomMedia, greenroomStylepackContent, templateTypeToShotMap]);

  const filteredVideos = useMemo(() => {
    return graphicItems.filter(
      (video) => !type || type === VideoType.all || (video.contentType && video.contentType === videoTypeMap[type])
    );
  }, [graphicItems, type]);

  useEffect(() => {
    Promise.allSettled([
      ...(stylepack?.videos ?? []).map(getContentTemplateDuration),
      ...filteredVideos.map(getGraphicDuration),
    ]).then((results) => {
      const durations: Record<string, number> = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          durations[result.value.video.id] = result.value.duration;
        } else {
          durations[result.reason.id] = 0;
        }
      }
      setDurations(durations);
    });
  }, [filteredVideos, stylepack?.videos]);

  const filtered = useMemo(() => {
    return filteredVideos.map(
      (video) =>
        ({
          ...video,
          duration: durations[video.id] ?? 0,
        }) satisfies GraphicItem
    );
  }, [filteredVideos, durations]);
  return filtered;
};
